/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

export const DEFAULT_PHONE_PREFIX = '+44'

const getPrefixDigits = (phonePrefix) => (phonePrefix || DEFAULT_PHONE_PREFIX).replace(/\D/g, '')

export const isValidInternationalPhone = (value, phonePrefix = DEFAULT_PHONE_PREFIX) => {
    if (!value || typeof value !== 'string') {
        return false
    }

    const trimmed = value.trim()
    if (!trimmed.startsWith('+')) {
        return false
    }

    const digits = trimmed.replace(/\D/g, '')
    const prefixDigitCount = getPrefixDigits(phonePrefix).length

    if (digits.length < prefixDigitCount + 4 || digits.length > 15) {
        return false
    }

    return true
}

export const parseDialCodeFromPhone = (phone) => {
    if (!phone || typeof phone !== 'string') {
        return null
    }

    const match = phone.trim().match(/^(\+\d{1,4})/)
    return match ? match[1] : null
}

export const getDefaultPhoneInputValue = (phonePrefix = DEFAULT_PHONE_PREFIX) => {
    const prefix = phonePrefix || DEFAULT_PHONE_PREFIX
    return `${prefix} `
}

export const formatPhoneNumber = (value, phonePrefix = DEFAULT_PHONE_PREFIX) => {
    if (!value) {
        return value
    }

    const prefix = phonePrefix || DEFAULT_PHONE_PREFIX
    const prefixDigits = getPrefixDigits(prefix)
    let digits = value.replace(/\D/g, '')

    if (digits.length === 0) {
        return ''
    }

    if (!value.trim().startsWith('+')) {
        if (digits.startsWith(prefixDigits)) {
            let national = digits.slice(prefixDigits.length)
            if (national.startsWith('0')) {
                national = national.slice(1)
            }
            digits = prefixDigits + national
        } else if (digits.startsWith('0')) {
            digits = prefixDigits + digits.slice(1)
        } else {
            digits = prefixDigits + digits
        }
    }

    digits = digits.slice(0, 15)

    const usesLocalePrefix = digits.startsWith(prefixDigits)
    const national = usesLocalePrefix ? digits.slice(prefixDigits.length) : digits

    if (usesLocalePrefix && prefix === '+44') {
        // CP-3132: +44 XXXX XXXXXX
        const ukNational = national.slice(0, 11)
        if (ukNational.length === 0) {
            return `${prefix} `
        }
        if (ukNational.length < 5) {
            return `+44 ${ukNational}`
        }
        return `+44 ${ukNational.slice(0, 4)} ${ukNational.slice(4)}`
    }

    if (usesLocalePrefix) {
        if (national.length === 0) {
            return `${prefix} `
        }
        return `${prefix} ${national}`.trim()
    }

    return `+${digits}`
}
